import {
  Route,
  createBrowserRouter,
  createRoutesFromElements,
} from 'react-router-dom';
import { lazy } from 'react';
import Layout from '../components/layout/Layout';
import ProtectedRoutes from './ProtectedRoutes';
import CreatorOnlyRoutes from './CreatorOnlyRoutes';
import PublicRoutes from './PublicRoutes';
import { UserProvider } from '../context/UserContext';
import { ROUTES } from './constants';
const AdminAnalytics = lazy(() => import('../pages/AdminAnalytics'));
import UsersTable from '@/components/dashboard/Admin/UsersTable';
import AdminUserDetail from '@/pages/AdminUserDetail';

import PublicLayout from '@/components/layout/PublicLayout';
import EditCampaign from '@/pages/EditCampaign';
// import PausedCampaign from '@/pages/PausedCampaign';
import { NotificationProvider } from '@/context/NotificationContext';
import { GoogleTagManager } from '@/components/Analytics';
import { ToastContainer } from 'react-toastify';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { ReactQueryDevtools } from '@tanstack/react-query-devtools';

/* ❌ NOT lazy */
import Dashboard from '../pages/Dashboard';
import UserManagement from '../pages/UserManagement';
import MyLinks from '../pages/MyLinks';
import BulkUpload from '../pages/BulkUpload';
import Analytics from '../pages/Analytics';
import CreateCampaign from '../pages/CreateCampaign';
import ScheduledCampaign from '../pages/ScheduledCampaign';
import ActiveCampaign from '../pages/ActiveCampaign';
import Campaign from '../pages/Campaign';
import CompletedCampaign from '../pages/CompletedCampaign';
import AnalyticsCampaign from '../pages/AnalyticsCampaign';
/* ✅ lazy pages */
const UserDetail = lazy(() => import('../pages/UserDetail'));
const WorldMapPage = lazy(() => import('../pages/WorldMapPage'));
const CampaignSmartlink = lazy(() => import('../pages/CampaignSmartlink'));
const AppDownloadPage = lazy(() => import('../pages/AppDownload'));
const Pricing = lazy(() => import('../pages/Plans'));
const Profile = lazy(() => import('../pages/profile/Profile'));
const MyWallet = lazy(() => import('../pages/MyWallet'));
const Signin = lazy(() => import('../pages/Signin'));
// const Signup = lazy(() => import('../pages/Signup'));
const SignupInfluencer = lazy(() => import('../pages/SignupInfluencer'));
const ForgetPassword = lazy(() => import('../pages/ForgetPassword'));
const Redirect = lazy(() => import('../pages/redirect'));
const SocialAuth = lazy(() => import('../pages/SocialAuth'));
const TokenRedirect = lazy(() => import('../pages/TokenRedirect'));
// const SignupBrand = lazy(() => import('../pages/SignupBrand'));
const ActivateAccount = lazy(() => import('../pages/ActivateAccount'));
const Checkout = lazy(() => import('../pages/stripe/Checkout'));
const YoutubeAuthCallback = lazy(() => import('../pages/YoutubeAuthCallback'));
const InstagramAuthCallback = lazy(
  () => import('../pages/InstagramAuthCallback'),
);
const TiktokAuthCallback = lazy(() => import('../pages/TiktokAuthCallback'));
const BrandSubscribers = lazy(() => import('../pages/BrandSubscribers'));
const Brand = lazy(() => import('../pages/Brand'));
const BrandDetail = lazy(() => import('../pages/BrandDetail'));
const CreatorSubscribers = lazy(() => import('../pages/CreatorSubscribers'));
const NewsletterSubscribers = lazy(
  () => import('../pages/NewsletterSubscribers'),
);
const FlaggedSubmissions = lazy(() => import('../pages/FlaggedSubmissions'));
const CashbackLedger = lazy(() => import('../pages/CashbackLedger'));
const CashbackDetail = lazy(() => import('../pages/CashbackDetail'));
const BonusLedger = lazy(() => import('../pages/BonusLedger'));
const QrCode = lazy(() => import('../pages/QrCode'));
const CreatorsWaitlist = lazy(() => import('../pages/CreatorsWaitlist'));
const CreatorDetailPage = lazy(() => import('../pages/CreatorDetailPage'));
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 5 * 60 * 1000,
      gcTime: 10 * 60 * 1000,
      retry: 1,
      refetchOnWindowFocus: false,
    },
  },
});

const CreatorSubmissionsPage = lazy(
  () => import('../pages/CreatorSubmissionsPage'),
);
const AdminCampaigns = lazy(() => import('../pages/AdminCampaigns'));
const AdminCampaignDetail = lazy(() => import('../pages/AdminCampaignDetail'));
const AdminCreators = lazy(() => import('../pages/AdminCreators'));
const CreatorPayouts = lazy(() => import('../pages/CreatorPayouts'));
const AllSubmissions = lazy(() => import('../pages/AllSubmissions'));

const Providers = ({ children }: { children: React.ReactNode }) => (
  <QueryClientProvider client={queryClient}>
    <GoogleTagManager />
    <NotificationProvider>
      <UserProvider>{children}</UserProvider>
    </NotificationProvider>
    <ToastContainer />
    {process.env.NODE_ENV === 'development' && (
      <ReactQueryDevtools initialIsOpen={false} />
    )}
  </QueryClientProvider>
);

export const router = createBrowserRouter(
  createRoutesFromElements(
    <>
      <Route element={<PublicRoutes />}>
        <Route path={ROUTES.WORLD_MAP} element={<WorldMapPage />} />
        <Route
          path={ROUTES.CAMPAIGN_SMARTLINK}
          element={<CampaignSmartlink />}
        />
        <Route path={ROUTES.QR_CODE} element={<QrCode />} />
        <Route path={ROUTES.APP_DOWNLOAD} element={<AppDownloadPage />} />
        <Route
          element={
            <Providers>
              <PublicLayout />
            </Providers>
          }
        >
          <Route path={ROUTES.SIGNIN} element={<Signin />} />
          <Route path={ROUTES.SIGNUP} element={<SignupInfluencer />} />
          <Route path={ROUTES.SOCIAL_AUTH} element={<SocialAuth />} />
          {/* <Route
            path={ROUTES.SIGNUP_INFLUENCER}
            element={<SignupInfluencer />}
          /> */}
          {/* <Route path={ROUTES.SIGNUP_BRAND} element={<SignupBrand />} /> */}
          <Route path={ROUTES.FORGET_PASSWORD} element={<ForgetPassword />} />
          <Route path={ROUTES.ACTIVATE_ACCOUNT} element={<ActivateAccount />} />
        </Route>
      </Route>

      <Route path={ROUTES.REDIRECT} element={<Redirect />} />
      <Route path={ROUTES.TOKEN_REDIRECT} element={<TokenRedirect />} />

      <Route
        element={
          <Providers>
            <ProtectedRoutes />
          </Providers>
        }
      >
        <Route path={ROUTES.HOME} element={<Layout />}>
          <Route index element={<Dashboard />} />
          <Route element={<CreatorOnlyRoutes />}>
            <Route path={ROUTES.MY_LINKS} element={<MyLinks />} />
            <Route path={ROUTES.BULK_UPLOAD} element={<BulkUpload />} />
            <Route path={ROUTES.ANALYTICS} element={<Analytics />} />
          </Route>
          <Route path={ROUTES.USER_MANAGEMENT} element={<UserManagement />} />
          <Route path={ROUTES.CREATE_CAMPAIGN} element={<CreateCampaign />} />
          <Route path={ROUTES.EDIT_CAMPAIGN} element={<EditCampaign />} />
          <Route
            path={ROUTES.SCHEDULED_CAMPAIGN}
            element={<ScheduledCampaign />}
          />
          <Route path={ROUTES.ACTIVE_CAMPAIGN} element={<ActiveCampaign />} />
          <Route
            path={ROUTES.COMPLETED_CAMPAIGN}
            element={<CompletedCampaign />}
          />
          <Route
            path={ROUTES.ANALYTICS_CAMPAIGN}
            element={<AnalyticsCampaign />}
          />
          <Route path={ROUTES.CAMPAIGN} element={<Campaign />} />
          <Route path={ROUTES.USER_DETAIL} element={<UserDetail />} />
          <Route path={ROUTES.PROFILE} element={<Profile />} />
          <Route path={ROUTES.WALLET} element={<MyWallet />} />
          <Route path={ROUTES.PLANS} element={<Pricing />} />
          <Route path={ROUTES.CHECKOUT} element={<Checkout />} />
          <Route
            path={ROUTES.YOUTUBE_AUTH_CALLBACK}
            element={<YoutubeAuthCallback />}
          />
          <Route
            path={ROUTES.INSTAGRAM_AUTH_CALLBACK}
            element={<InstagramAuthCallback />}
          />
          <Route
            path={ROUTES.TIKTOK_AUTH_CALLBACK}
            element={<TiktokAuthCallback />}
          />
          <Route path="/admin/users" element={<UsersTable />} />
          <Route path="/admin/users/:userId" element={<AdminUserDetail />} />
          <Route
            path={ROUTES.BRAND_SUBSCRIBERS}
            element={<BrandSubscribers />}
          />
          <Route path={ROUTES.BRAND} element={<Brand />} />
          <Route path={ROUTES.ADMIN_BRANDS} element={<Brand />} />
          <Route path={ROUTES.BRAND_DETAIL} element={<BrandDetail />} />
          <Route
            path={ROUTES.CREATOR_SUBSCRIBERS}
            element={<CreatorSubscribers />}
          />
          <Route
            path={ROUTES.NEWSLETTER_SUBSCRIBERS}
            element={<NewsletterSubscribers />}
          />
          <Route
            path={ROUTES.FLAGGED_SUBMISSIONS}
            element={<FlaggedSubmissions />}
          />
          <Route path={ROUTES.CASHBACK_LEDGER} element={<CashbackLedger />} />
          <Route path={ROUTES.CASHBACK_DETAIL} element={<CashbackDetail />} />
          <Route path={ROUTES.BONUS_LEDGER} element={<BonusLedger />} />
          <Route
            path={ROUTES.CREATORS_WAITLIST}
            element={<CreatorsWaitlist />}
          />
          <Route
            path={ROUTES.CREATORS_DETAIL}
            element={<CreatorDetailPage />}
          />
          <Route
            path={ROUTES.CREATOR_SUBMISSIONS}
            element={<CreatorSubmissionsPage />}
          />
          <Route path={ROUTES.ADMIN_ANALYTICS} element={<AdminAnalytics />} />
          <Route path={ROUTES.ADMIN_CAMPAIGNS} element={<AdminCampaigns />} />
          <Route
            path={ROUTES.ADMIN_CAMPAIGN_DETAIL}
            element={<AdminCampaignDetail />}
          />
          <Route path={ROUTES.ADMIN_CREATORS} element={<AdminCreators />} />
          <Route
            path={ROUTES.ADMIN_CREATOR_DETAIL}
            element={<CreatorDetailPage />}
          />
          <Route path={ROUTES.CREATOR_PAYOUTS} element={<CreatorPayouts />} />
          <Route path={ROUTES.ADMIN_SUBMISSIONS} element={<AllSubmissions />} />
        </Route>
      </Route>
    </>,
  ),
);

const RoutesConfig = () => {
  return <></>;
};

export default RoutesConfig;
